import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Chip,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import IconButton from '@mui/material/IconButton';
import { PortalHomepageSeoItem, SeoSnapshotBackup, ScheduledReleaseItem } from '../../types/seo';
import SeoAuditScoreCard from './SeoAuditScoreCard';
import RefineRegenerateModal from './RefineRegenerateModal';
import SafeSyncQueueModal from './SafeSyncQueueModal';
import { calculateSeoScore } from '../../utils/seoAIEngine';

interface PortalHomepageSeoTabProps {
  homepageSeo: PortalHomepageSeoItem;
  backups: SeoSnapshotBackup[];
  onUpdateHomepage: (updated: Partial<PortalHomepageSeoItem>) => void;
  onImmediateSync: (snapshot: SeoSnapshotBackup) => void;
  onQueueRelease: (queueItem: ScheduledReleaseItem) => void;
  onRollback: (backup: SeoSnapshotBackup) => void;
}

export default function PortalHomepageSeoTab({
  homepageSeo,
  backups,
  onUpdateHomepage,
  onImmediateSync,
  onQueueRelease,
  onRollback,
}: PortalHomepageSeoTabProps) {
  const [activeSubTab, setActiveSubTab] = useState<number>(0);
  const [htmlViewMode, setHtmlViewMode] = useState<'preview' | 'code'>('preview');

  const [refineModalOpen, setRefineModalOpen] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const scoreData = calculateSeoScore({
    title: homepageSeo.seoTitle,
    description: homepageSeo.metaDescription,
    hasFaqs: homepageSeo.faqs.length >= 2,
    hasSchema: !!homepageSeo.schemaJson?.['@context'],
    hasRera: homepageSeo.metaDescription.toLowerCase().includes('rera') || homepageSeo.brandStoryHtml.toLowerCase().includes('gujrera'),
    hasVastu: true,
    hasSuratMention: homepageSeo.seoTitle.toLowerCase().includes('surat') && homepageSeo.metaDescription.toLowerCase().includes('surat'),
    contentWordCount: homepageSeo.brandStoryHtml.split(' ').length,
  });

  const handleTitleChange = (val: string) => {
    onUpdateHomepage({ seoTitle: val, syncStatus: 'draft' });
  };

  const handleDescChange = (val: string) => {
    onUpdateHomepage({ metaDescription: val, syncStatus: 'draft' });
  };

  const handleStoryChange = (val: string) => {
    onUpdateHomepage({ brandStoryHtml: val, syncStatus: 'draft' });
  };

  const handleAddFaq = () => {
    const newFaqs = [
      ...homepageSeo.faqs,
      {
        question: 'Are there hidden brokerage charges when buying from Swarna Group?',
        answer: 'No. All units listed on www.swarnagroup.in are 100% direct developer inventory with absolute zero brokerage fees.',
      },
    ];
    onUpdateHomepage({ faqs: newFaqs, syncStatus: 'draft' });
  };

  const handleRemoveFaq = (index: number) => {
    const newFaqs = homepageSeo.faqs.filter((_, idx) => idx !== index);
    onUpdateHomepage({ faqs: newFaqs, syncStatus: 'draft' });
  };

  const handleFaqChange = (index: number, field: 'question' | 'answer', val: string) => {
    const newFaqs = homepageSeo.faqs.map((f, idx) => (idx === index ? { ...f, [field]: val } : f));
    onUpdateHomepage({ faqs: newFaqs, syncStatus: 'draft' });
  };

  const handleApplyRefinement = (refined: {
    title: string;
    description: string;
    richContent?: string;
    faqs?: { question: string; answer: string }[];
  }) => {
    onUpdateHomepage({
      seoTitle: refined.title,
      metaDescription: refined.description,
      ...(refined.richContent ? { brandStoryHtml: refined.richContent } : {}),
      ...(refined.faqs ? { faqs: refined.faqs } : {}),
      syncStatus: 'draft',
      lastEditedAt: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) + ' IST',
    });
  };

  const handleCopySchema = () => {
    navigator.clipboard.writeText(JSON.stringify(homepageSeo.schemaJson, null, 2));
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2500);
  };

  return (
    <Box>
      {/* Top Controls */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid #e8e8e8', bgcolor: '#fff' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <BusinessIcon sx={{ color: '#9d6f32', fontSize: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Portal Homepage SEO & E-E-A-T Authority Hub (www.swarnagroup.in)
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Establishes Surat developer brand authority, GujRERA credibility, Sitelinks SearchBox, and root domain indexability.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => setRefineModalOpen(true)}
              sx={{ color: '#9d6f32', borderColor: '#9d6f32', fontWeight: 700, textTransform: 'none' }}
            >
              ↻ Re-Generate AI
            </Button>
            <Button
              variant="contained"
              startIcon={<ShieldOutlinedIcon />}
              onClick={() => setSyncModalOpen(true)}
              sx={{ bgcolor: '#2e7d32', fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#1b5e20' } }}
            >
              Safe Sync & Rollback
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e8e8e8', bgcolor: '#fff', mb: 3 }}>
            <Tabs
              value={activeSubTab}
              onChange={(_, v) => setActiveSubTab(v)}
              sx={{ borderBottom: '1px solid #e8e8e8', mb: 2.5 }}
            >
              <Tab label="1. Homepage Metadata & SERP" sx={{ fontWeight: 700 }} />
              <Tab label="2. 200-Word E-E-A-T Brand Story HTML" sx={{ fontWeight: 700 }} />
              <Tab label="3. Brand Trust FAQs" sx={{ fontWeight: 700 }} />
              <Tab label="4. Schema.org RealEstateAgent & WebSite" sx={{ fontWeight: 700 }} />
            </Tabs>

            {/* Tab 0: Metadata */}
            {activeSubTab === 0 && (
              <Stack spacing={2.5}>
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#444' }}>
                      Homepage SEO Meta Title (≤ 60 chars)
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: homepageSeo.seoTitle.length <= 60 ? '#2e7d32' : '#d32f2f' }}>
                      {homepageSeo.seoTitle.length}/60 chars
                    </Typography>
                  </Stack>
                  <TextField fullWidth size="small" value={homepageSeo.seoTitle} onChange={(e) => handleTitleChange(e.target.value)} />
                </Box>

                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#444' }}>
                      Homepage Meta Description (Strictly 125 – 155 chars)
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: homepageSeo.metaDescription.length >= 125 && homepageSeo.metaDescription.length <= 155 ? '#2e7d32' : '#ed6c02' }}>
                      {homepageSeo.metaDescription.length} chars
                    </Typography>
                  </Stack>
                  <TextField fullWidth multiline rows={3} value={homepageSeo.metaDescription} onChange={(e) => handleDescChange(e.target.value)} />
                </Box>

                {/* SERP Simulator */}
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fbfbfb', borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>
                    Google Brand Authority SERP Preview
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ color: '#202124', display: 'block', fontSize: 11 }}>
                      https://www.swarnagroup.in
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#1a0dab', fontWeight: 500, fontSize: 18, lineHeight: 1.3 }}>
                      {homepageSeo.seoTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#4d5156', fontSize: 13, mt: 0.5, lineHeight: 1.4 }}>
                      {homepageSeo.metaDescription}
                    </Typography>
                  </Box>
                </Paper>
              </Stack>
            )}

            {/* Tab 1: Brand Story */}
            {activeSubTab === 1 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#444' }}>
                    200-Word Brand Story & Trust Overview (HTML)
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant={htmlViewMode === 'preview' ? 'contained' : 'outlined'}
                      startIcon={<VisibilityIcon />}
                      onClick={() => setHtmlViewMode('preview')}
                      sx={{ fontSize: 11, height: 28 }}
                    >
                      Visual Preview
                    </Button>
                    <Button
                      size="small"
                      variant={htmlViewMode === 'code' ? 'contained' : 'outlined'}
                      startIcon={<CodeIcon />}
                      onClick={() => setHtmlViewMode('code')}
                      sx={{ fontSize: 11, height: 28 }}
                    >
                      HTML Code
                    </Button>
                  </Stack>
                </Stack>

                {htmlViewMode === 'preview' ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: '#fff',
                      lineHeight: 1.6,
                      fontSize: 14,
                      color: '#333',
                      '& h3': { color: '#9d6f32', fontSize: 17, mt: 0, mb: 1.5 },
                      '& p': { mb: 1.5 },
                    }}
                    dangerouslySetInnerHTML={{ __html: homepageSeo.brandStoryHtml }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    rows={10}
                    value={homepageSeo.brandStoryHtml}
                    onChange={(e) => handleStoryChange(e.target.value)}
                    sx={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                )}
              </Box>
            )}

            {/* Tab 2: Brand FAQs */}
            {activeSubTab === 2 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Developer Authority & Buyer Trust FAQs ({homepageSeo.faqs.length})
                  </Typography>
                  <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={handleAddFaq} sx={{ color: '#9d6f32', fontWeight: 700 }}>
                    + Add FAQ
                  </Button>
                </Stack>

                <Stack spacing={2}>
                  {homepageSeo.faqs.map((faq, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafaf9' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                        <TextField
                          label={`Question #${idx + 1}`}
                          fullWidth
                          size="small"
                          value={faq.question}
                          onChange={(e) => handleFaqChange(idx, 'question', e.target.value)}
                          sx={{ mr: 1, bgcolor: '#fff' }}
                        />
                        <IconButton size="small" color="error" onClick={() => handleRemoveFaq(idx)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <TextField
                        label="Answer"
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                        value={faq.answer}
                        onChange={(e) => handleFaqChange(idx, 'answer', e.target.value)}
                        sx={{ bgcolor: '#fff' }}
                      />
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Tab 3: Schema */}
            {activeSubTab === 3 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#444' }}>
                    Schema.org RealEstateAgent & WebSite Sitelinks SearchBox JSON-LD
                  </Typography>
                  <Button size="small" startIcon={<ContentCopyIcon />} onClick={handleCopySchema} sx={{ color: '#9d6f32', fontWeight: 700 }}>
                    {copySuccess ? 'Copied JSON-LD!' : 'Copy Structured Data'}
                  </Button>
                </Stack>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#1e1e1e', color: '#4ec9b0', fontFamily: 'monospace', fontSize: 12, borderRadius: 2, maxHeight: 320, overflow: 'auto' }}>
                  <pre style={{ margin: 0 }}>{JSON.stringify(homepageSeo.schemaJson, null, 2)}</pre>
                </Paper>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right Column: Score */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Stack spacing={3}>
            <SeoAuditScoreCard score={scoreData.score} checks={scoreData.checks} />

            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e8e8e8', bgcolor: '#fff' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                Portal Domain Authority
              </Typography>
              <Stack spacing={1.5} sx={{ fontSize: 13 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Root Portal:</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#9d6f32' }}>{homepageSeo.portalUrl}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Target Territory:</Typography>
                  <Typography sx={{ fontWeight: 700 }}>{homepageSeo.targetCity}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Active Projects:</Typography>
                  <Typography sx={{ fontWeight: 700 }}>2 (Ready & Dec 2026)</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Status:</Typography>
                  <Chip label={homepageSeo.syncStatus.toUpperCase()} size="small" color={homepageSeo.syncStatus === 'synced' ? 'success' : 'warning'} sx={{ fontWeight: 700, height: 20 }} />
                </Box>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      {/* Refine Modal */}
      <RefineRegenerateModal
        open={refineModalOpen}
        onClose={() => setRefineModalOpen(false)}
        entityType="HOMEPAGE"
        currentTitle={homepageSeo.seoTitle}
        currentDescription={homepageSeo.metaDescription}
        currentRichContent={homepageSeo.brandStoryHtml}
        currentFaqs={homepageSeo.faqs}
        projectName="Swarna Group"
        locality="Surat, Gujarat"
        onApply={handleApplyRefinement}
      />

      {/* Safe Sync Modal */}
      <SafeSyncQueueModal
        open={syncModalOpen}
        onClose={() => setSyncModalOpen(false)}
        entityId={homepageSeo.id}
        entityType="HOMEPAGE"
        entityName="Portal Homepage (www.swarnagroup.in)"
        currentData={{
          seoTitle: homepageSeo.seoTitle,
          metaDescription: homepageSeo.metaDescription,
          richContentHtml: homepageSeo.brandStoryHtml,
          faqs: homepageSeo.faqs,
          schemaJson: homepageSeo.schemaJson,
        }}
        backups={backups}
        onImmediateSync={(snapshot) => {
          onImmediateSync(snapshot);
          onUpdateHomepage({ syncStatus: 'synced', lastSyncedAt: snapshot.timestamp });
        }}
        onQueueRelease={(queueItem) => {
          onQueueRelease(queueItem);
          onUpdateHomepage({ syncStatus: 'staged' });
        }}
        onRollback={(backup) => {
          onRollback(backup);
          onUpdateHomepage({
            seoTitle: backup.snapshotData.seoTitle,
            metaDescription: backup.snapshotData.metaDescription,
            brandStoryHtml: backup.snapshotData.richContentHtml,
            faqs: backup.snapshotData.faqs,
            schemaJson: backup.snapshotData.schemaJson,
            syncStatus: 'synced',
          });
        }}
      />
    </Box>
  );
}
